import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import PropTypes from "prop-types";
import { clearObjectHasNewComments } from "~/store/actions/actions.additionalObjectData.jsx";
import { getNewCommentsCountByParent } from "~/store/selectors/selector.comments.jsx";
import { useScrollIntoView } from "../Hooks/useScrollIntoView.js";
import ScrollToToaster from "../ScrollToToaster/ScrollToToaster.jsx";

const NewCommentsNotification = ({ parentType, parentId }) => {
  const { t } = useTranslation();
  const { scrollIntoViewByElement } = useScrollIntoView("comment new");
  const { scrollIntoViewByElement: scrollIntoCommentSection } = useScrollIntoView("object_comments");
  const dispatch = useDispatch();

  const newCommentsCount = useSelector((state) => getNewCommentsCountByParent(state, parentType, parentId));

  const scrollIntoCommentView = useCallback(() => {
    if (newCommentsCount > 0) {
      scrollIntoViewByElement();
    } else {
      scrollIntoCommentSection();
    }
  }, [newCommentsCount, scrollIntoViewByElement, scrollIntoCommentSection]);

  const [show, setShow] = useState(false);

  const handleOnClose = useCallback(() => {
    setShow(false);
    dispatch(clearObjectHasNewComments(parentType, parentId));
  }, [dispatch, parentId, parentType]);

  const timeoutRef = useRef();

  useUpdateEffect(() => {
    if (newCommentsCount > 0) {
      clearTimeout(timeoutRef.current);
      setShow(true);
      timeoutRef.current = setTimeout(handleOnClose, 15000);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [newCommentsCount, handleOnClose]);

  const handleClick = useCallback(() => {
    scrollIntoCommentView();
    handleOnClose();
  }, [handleOnClose, scrollIntoCommentView]);

  if (!show) {
    return null;
  }

  return (
    <ScrollToToaster
      message={newCommentsCount === 1 ? t("New Comment") : t("New Comments")}
      onClose={handleOnClose}
      onClick={handleClick}
    />
  );
};

NewCommentsNotification.propTypes = {
  parentType: PropTypes.string.isRequired,
  parentId: PropTypes.number.isRequired,
};

NewCommentsNotification.displayName = "NewCommentsNotification";
export default NewCommentsNotification;
